import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonRefresherContent,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonSelect,
  IonSelectOption,
  IonSearchbar,
  IonPopover,
  IonFab,
  IonSpinner,
} from '@ionic/react';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { person, time, checkmarkCircle, alertCircle, closeCircle, searchOutline, swapVertical, closeCircleOutline, closeOutline } from 'ionicons/icons';
import styles from './LeadsListModal.module.scss';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import PositionedRefresher from './PositionedRefresher';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import { fetchLeadsFromApi, type GlsaLeadDoc } from '../services/glsaLeadsService';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

interface LeadsListModalProps {
  onClose: () => void;
}


const LeadsListModal: React.FC<LeadsListModalProps> = ({ onClose }) => {
  const { t: tr } = useTranslation();
  const [leads, setLeads] = useState<GlsaLeadDoc[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name-az' | 'name-za' | 'date-newest' | 'date-oldest'>('date-newest');
  const [showSortPopover, setShowSortPopover] = useState(false);
  const [sortPopoverEvent, setSortPopoverEvent] = useState<Event | undefined>(undefined);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const wasAtTop = useRef(true);
  const rafTick = useRef(false);

  const titleCardRef = useRef<HTMLDivElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const scrollContainerRef = useRef<HTMLIonGridElement>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);

  useEffect(() => {
    Promise.resolve().then(() => {
      setLeadsLoading(true);
      fetchLeadsFromApi(100)
        .then(docs => { setLeads(docs); setLeadsError(null); })
        .catch(err => { setLeadsError(err instanceof Error ? err.message : tr('components.LeadsListModal.failedToLoadLeads')); })
        .finally(() => setLeadsLoading(false));
    });
  }, [tr]);

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'BOOKED': return checkmarkCircle;
      case 'ACTIVE':
      case 'NEW': return alertCircle;
      case 'DECLINED':
      case 'EXPIRED':
      case 'DISABLED': return closeCircle;
      default: return alertCircle;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'BOOKED': return 'success';
      case 'ACTIVE': return 'warning';
      case 'NEW': return 'primary';
      case 'DECLINED':
      case 'EXPIRED':
      case 'DISABLED': return 'danger';
      default: return 'medium';
    }
  };

  const processedLeads = (() => {
    let filtered = leads.filter(lead => {
      const s = searchText.toLowerCase();
      return (
        lead.contactName.toLowerCase().includes(s) ||
        lead.contactPhone.toLowerCase().includes(s) ||
        lead.jobType.toLowerCase().includes(s) ||
        lead.leadCategory.toLowerCase().includes(s)
      );
    });

    if (activeFilter !== 'all') {
      const lf = activeFilter.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.status.toLowerCase() === lf ||
        lead.jobType.toLowerCase() === lf ||
        lead.leadCategory.toLowerCase() === lf
      );
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name-az': return a.contactName.localeCompare(b.contactName);
        case 'name-za': return b.contactName.localeCompare(a.contactName);
        case 'date-newest': {
          const ta = new Date(a.createdAt ?? 0).getTime();
          const tb = new Date(b.createdAt ?? 0).getTime();
          return tb - ta;
        }
        case 'date-oldest': {
          const ta = new Date(a.createdAt ?? 0).getTime();
          const tb = new Date(b.createdAt ?? 0).getTime();
          return ta - tb;
        }
        default: return 0;
      }
    });
  })();

  // Overlay positioning
  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  // Keyboard handling
  useEffect(() => {
    const scrollContainerEl = scrollContainerRef.current;
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (kbHeight: number) => {
      const targetBottom = kbHeight > 0 ? kbHeight + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);
        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKeyboardOffset = (kbHeight: number) => {
      const grid = scrollContainerRef.current;
      if (grid) {
        if (kbHeight > 0) { grid.style.paddingBottom = `${kbHeight + 60}px`; }
        else { grid.style.removeProperty('padding-bottom'); }
      }
      animateBottomTo(kbHeight);
    };

    try {
      Keyboard.addListener('keyboardWillShow', (info) => applyKeyboardOffset(info.keyboardHeight || 0)).then(h => { didShowHandle = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', (info) => applyKeyboardOffset(info.keyboardHeight || 0)).catch(() => { });
      Keyboard.addListener('keyboardWillHide', () => applyKeyboardOffset(0)).then(h => { didHideHandle = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', () => applyKeyboardOffset(0)).catch(() => { });
    } catch { }

    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleViewportResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        applyKeyboardOffset(offset > 50 ? offset : 0);
      });
    };
    if (vv) vv.addEventListener('resize', handleViewportResize);
    const handleVisibilityChange = () => { if (document.visibilityState === 'visible') setTimeout(handleViewportResize, 300); };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      const grid = scrollContainerEl;
      if (grid) grid.style.removeProperty('padding-bottom');
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch { }
      try { didHideHandle?.remove(); } catch { }
    };
  }, []);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      if (searchRef.current) { try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { } }
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => { setIsSearchHidden(true); setIsSearchClosing(false); }, 220);
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) { getter().then((el: HTMLInputElement) => { try { el.blur(); } catch { } try { Keyboard.hide(); } catch { } }).catch(() => { try { Keyboard.hide(); } catch { } }); }
        else { try { Keyboard.hide(); } catch { } }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (getter) { getter().then((el: HTMLInputElement) => { try { el.focus(); } catch { } }).catch(() => { }); }
        } catch { }
      }));
    }
  };

  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    let headerEl: HTMLElement | null = null;
    const modalHost = scrollContainerRef.current?.closest('ion-modal') as HTMLElement | null;
    if (modalHost) { headerEl = modalHost.querySelector<HTMLElement>('[data-header-title]'); }
    if (!headerEl) {
      const contentHost = scrollContainerRef.current?.closest('ion-content') as HTMLElement | null;
      headerEl = contentHost?.querySelector<HTMLElement>('[data-header-title]') ?? document.querySelector<HTMLElement>('[data-header-title]');
    }

    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardRef.current) titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      setTimeout(() => { if (headerEl) headerEl.style.transition = ''; if (titleCardRef.current) titleCardRef.current.style.transition = ''; }, 220);
    }

    headerEl?.style.setProperty('--title-opacity', String(progress));
    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(scrollContainerRef, 66, applyProgress);

  return (
    <div className={styles.modalWrapper}>
      <BasePageWithHeader
        title={tr('components.LeadsListModal.leadsList')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeCircleOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton shape="round" className={styles.searchButton} onClick={handleToggleSearch}>
            <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
          </IonButton>
        }
      >
        <IonContent ref={ionContentRef}>
          <PositionedRefresher
            titleRef={titleCardRef}
            contentRef={scrollContainerRef}
            offset={0}
            anchor="bottom"
            id="leads-list-refresher"
            className={styles.leadsRefresher}
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={async (e) => {
              try { const data = await fetchLeadsFromApi(100); setLeads(data); } catch (err) { console.warn('[LeadsListModal] sync error', err); }
              e.detail.complete();
            }}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

          <IonGrid
            ref={scrollContainerRef}
            className={styles.leadListContainer}
            onScroll={(e) => {
              const top = (e.target as HTMLElement).scrollTop || 0;
              const atTop = top <= 5;
              if (atTop !== wasAtTop.current) { wasAtTop.current = atTop; setRefresherDisabled(!atTop); }
              if (!rafTick.current) {
                rafTick.current = true;
                requestAnimationFrame(() => { handleScroll(top); rafTick.current = false; });
              }
            }}
          >
            <div ref={titleCardRef} className={styles.titleCardWrapper}>
              <TitleCard
                title={tr('components.LeadsListModal.leadsList')}
                cardClassName={styles.leadsTitleCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.leadsTitle}
              />
            </div>

            {/* Filter & Sort Controls */}
            <IonItem className={styles.filterControls}>
              <IonSelect
                interface="alert"
                interfaceOptions={{ cssClass: 'customAlert', backdropDismiss: true, header: tr('components.LeadsListModal.filterBy') }}
                value={activeFilter}
                onIonChange={(e) => setActiveFilter(e.detail.value)}
                className={styles.sortSelect}
              >
                <IonSelectOption value="all">{tr('components.LeadsListModal.allLeads')}</IonSelectOption>
                <IonSelectOption value="new">{tr('components.LeadsListModal.new')}</IonSelectOption>
                <IonSelectOption value="active">{tr('components.LeadsListModal.active')}</IonSelectOption>
                <IonSelectOption value="booked">{tr('components.LeadsListModal.booked')}</IonSelectOption>
                <IonSelectOption value="declined">{tr('components.LeadsListModal.declined')}</IonSelectOption>
                <IonSelectOption value="expired">{tr('components.LeadsListModal.expired')}</IonSelectOption>
                <IonSelectOption value="disabled">{tr('components.LeadsListModal.disabled')}</IonSelectOption>
              </IonSelect>

              <IonButton
                fill="clear"
                shape="round"
                expand="block"
                onClick={(e) => { setSortPopoverEvent(e.nativeEvent); setShowSortPopover(true); }}
                className={styles.sortOrderButton}
                slot="end"
              >
                <IonIcon icon={swapVertical} />
              </IonButton>

              <IonPopover
                isOpen={showSortPopover}
                event={sortPopoverEvent}
                onDidDismiss={() => setShowSortPopover(false)}
                className={styles.sortPopover}
              >
                <IonList>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('date-newest'); setShowSortPopover(false); }} className={sortBy === 'date-newest' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.LeadsListModal.dateNewestFirst')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('date-oldest'); setShowSortPopover(false); }} className={sortBy === 'date-oldest' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.LeadsListModal.dateOldestFirst')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('name-az'); setShowSortPopover(false); }} className={sortBy === 'name-az' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.LeadsListModal.nameAZ')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('name-za'); setShowSortPopover(false); }} className={sortBy === 'name-za' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.LeadsListModal.nameZA')}</IonLabel>
                  </IonItem>
                </IonList>
              </IonPopover>
            </IonItem>

            {leadsLoading && (
              <div className={styles.emptyState}>
                <IonSpinner name="bubbles" />
              </div>
            )}

            {leadsError && !leadsLoading && (
              <div className={styles.emptyState}>
                <IonText color="danger"><p>{leadsError}</p></IonText>
              </div>
            )}

            {!leadsLoading && !leadsError && (
              <IonList className={styles.leadList}>
                {processedLeads.map((lead) => (
                  <IonItem
                    key={lead.id}
                    className={styles.leadItem}
                    button
                    detail={false}
                    lines="none"
                  >
                    <div slot="start" className={styles.leadAvatarCircle}>
                      <IonIcon icon={person} className={styles.leadAvatarIcon} />
                    </div>
                    <IonLabel>
                      <h2 className={styles.leadName}>{lead.contactName}</h2>
                      <p className={styles.leadEmail}>{lead.contactPhone}</p>
                      <div className={styles.leadDetails}>
                        <IonGrid>
                          <IonRow>
                            <IonCol size="6">
                              <div className={styles.detailItem}>
                                <IonIcon icon={time} />
                                <span>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString(intlLocale()) : '—'}</span>
                              </div>
                            </IonCol>
                            <IonCol size="6">
                              <div className={styles.detailItem}>
                                <IonText color="medium"><small>{lead.jobType || lead.leadCategory || '—'}</small></IonText>
                              </div>
                            </IonCol>
                          </IonRow>
                          {lead.leadPrice > 0 && (
                            <IonRow>
                              <IonCol>
                                <IonText color="medium">
                                  <small>{tr('components.LeadsListModal.leadPrice', { currency: lead.currency })}{lead.leadPrice.toFixed(2)}</small>
                                </IonText>
                              </IonCol>
                            </IonRow>
                          )}
                        </IonGrid>
                      </div>
                    </IonLabel>
                    <div className={styles.badgeContainer}>
                      <IonBadge color={getStatusColor(lead.status)} className={styles.statusBadge}>
                        <IonIcon icon={getStatusIcon(lead.status)} />
                        {lead.status.charAt(0).toUpperCase() + lead.status.slice(1).toLowerCase()}
                      </IonBadge>
                    </div>
                  </IonItem>
                ))}
              </IonList>
            )}

            {!leadsLoading && !leadsError && processedLeads.length === 0 && (
              <div className={styles.emptyState}>
                <IonText color="medium"><p>{leads.length === 0 ? tr('components.LeadsListModal.noLeadsYetPullDownTo') : tr('components.LeadsListModal.noLeadsMatchYourSearch')}</p></IonText>
              </div>
            )}

            {isSearchVisible && <div className={styles.searchBarSpacer} />}
          </IonGrid>
        </IonContent>

        {/* Morphing search overlay */}
        <IonFab
          ref={el => { overlayRef.current = el; }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={50}
            onIonInput={e => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={tr('components.LeadsListModal.searchLead', { length: leads.length, v2: leads.length !== 1 ? 's' : '' })}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>
    </div>
  );
};

export default LeadsListModal;